import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

/**
 * PWA Install prompt — appears when browser fires beforeinstallprompt
 * (Chrome/Edge Android, Chrome desktop, Samsung Internet).
 * iOS Safari doesn't fire this event: shows an instructions banner instead.
 */
export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Detect iOS Safari (no beforeinstallprompt)
    const ua = window.navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    if (isStandalone) return undefined; // already installed
    if (localStorage.getItem('install_dismissed_v1') === '1') return undefined;

    if (iOS) {
      setIsIos(true);
      // show iOS install hint after 5s so it's not intrusive
      const t = setTimeout(() => setVisible(true), 5000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setVisible(false));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem('install_dismissed_v1', '1');
  };

  if (!visible || dismissed) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-md rounded-2xl bg-primary text-primary-foreground shadow-2xl p-4 flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
        <Smartphone className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Instala DEAL como app nativa</p>
        {isIos ? (
          <p className="text-xs mt-1 opacity-90">
            Toca <span className="inline-flex items-center px-1 rounded bg-white/20">⎋</span> Compartir y luego <b>Añadir a pantalla de inicio</b>.
          </p>
        ) : (
          <p className="text-xs mt-1 opacity-90">Tenla en tu home screen con acceso rápido y notificaciones.</p>
        )}
        {!isIos && (
          <Button
            data-testid="install-app-btn"
            size="sm"
            onClick={install}
            className="mt-2 bg-white text-brand-deep dark:text-brand-deep dark:text-brand-green hover:bg-white/90 text-xs h-8"
          >
            <Download className="w-3 h-3 mr-1" /> Instalar ahora
          </Button>
        )}
      </div>
      <button
        onClick={dismiss}
        className="p-1 rounded-full hover:bg-white/20"
        aria-label="Cerrar"
        data-testid="install-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
